// Deteccao de loja por URL (logica pura, sem rede, sem dependencias).
//
// Usado em tres lugares com a MESMA regra: detect() dos providers (bot e edge),
// a edge convert-link e o painel (chips "loja detectada" ao colar o link).
// Nao importa providers: o painel roda no browser e nao pode puxar node:crypto.

import { storeOfShortener } from "./shorteners.ts";

export type StoreCode =
  | "amazon"
  | "mercadolivre"
  | "magalu"
  | "shopee"
  | "aliexpress"
  | "awin"
  | "shein"
  | "rakuten";

export const STORE_CODES: readonly StoreCode[] = [
  "amazon",
  "mercadolivre",
  "magalu",
  "shopee",
  "aliexpress",
  "shein",
  "awin",
  "rakuten",
];

const AMAZON_HOST = /(?:^|\.)amazon\.com(?:\.br)?$/i;
const ML_HOST =
  /(?:^|\.)mercadolivre\.com(?:\.[a-z]{2,3})*$|^mercado\.livre\.com\.br$|(?:^|\.)(?:meli\.la|meli\.io|mercadol\.iv)$/i;
const ML_PATH = /\/(?:social|sec)\/|\/p\/MLB|\/MLB-/i;
const MAGALU_HOST =
  /(?:^|\.)(?:magazineluiza\.com\.br|magalu\.com\.br|magalu\.com|mgl\.to|magazinevoce\.com\.br|parceiromagalu\.com\.br)$/i;
const MAGALU_PRODUCT_PATH = /(?:^|\/)(?:produto|p)(?:\/|$)/i;
const MAGAZINEVOCE_PRODUCT_PATH = /\/p\/[a-z0-9-]{1,}(?:\/|$)/i;
const SHOPEE_HOST = /(?:^|\.)shopee\.com(?:\.br)?$/i;
const ALIEXPRESS_HOST = /(?:^|\.)aliexpress\.com(?:\.br)?$/i;
const AWIN_HOST = /(?:^|\.)(?:awin1\.com|aw\.de)$/i;
const SHEIN_HOST = /(?:^|\.)shein\.com(?:\.[a-z]{2})?$/i;
const SHEIN_CANONICAL_PATH = /-p-\d+\.html/i;
const RAKUTEN_HOST = /(?:^|\.)(?:linksynergy\.com|rakutenadvertising\.com)$/i;

function parse(url: string): URL | null {
  try {
    const parsed = new URL(url);
    return parsed.protocol === "http:" || parsed.protocol === "https:" ? parsed : null;
  } catch {
    return null;
  }
}

/** Regras de host por loja (nivel "a loja e esta", sem exigir pagina de produto). */
const HOST_RULES: ReadonlyArray<readonly [StoreCode, (url: URL) => boolean]> = [
  ["amazon", (u) => AMAZON_HOST.test(u.hostname)],
  ["mercadolivre", (u) => ML_HOST.test(u.hostname) || ML_PATH.test(u.pathname)],
  ["magalu", (u) => MAGALU_HOST.test(u.hostname)],
  ["shopee", (u) => SHOPEE_HOST.test(u.hostname)],
  ["aliexpress", (u) => ALIEXPRESS_HOST.test(u.hostname)],
  ["awin", (u) => AWIN_HOST.test(u.hostname)],
  ["shein", (u) => SHEIN_HOST.test(u.hostname)],
  ["rakuten", (u) => RAKUTEN_HOST.test(u.hostname)],
];

/** Loja da URL pelo host. null = loja nao reconhecida (pode ser marca Awin/Rakuten). */
export function detectStoreCode(url: string): StoreCode | null {
  const parsed = parse(url);
  if (!parsed) {
    return null;
  }
  for (const [code, matches] of HOST_RULES) {
    if (matches(parsed)) {
      return code;
    }
  }
  return null;
}

/** Testa a regra de UMA loja (independente da ordem das demais regras). */
export function matchesStore(url: string, code: StoreCode): boolean {
  const parsed = parse(url);
  const rule = HOST_RULES.find(([ruleCode]) => ruleCode === code);
  return Boolean(parsed && rule && rule[1](parsed));
}

/** Loja provavel para a UI: host da loja ou encurtador proprio dela (amzn.to). */
export function detectStoreHint(url: string): StoreCode | null {
  return detectStoreCode(url) ?? (storeOfShortener(url) as StoreCode | null);
}

/** Lojas cujo ToS proibe encurtador/redirect de terceiros (inclusive o nosso). */
const DIRECT_ONLY_STORES: readonly StoreCode[] = ["amazon", "mercadolivre", "magalu"];

/**
 * true se a URL e de loja que exige link direto, pelo host da loja OU por um
 * encurtador proprio dela (a.co, amzn.to, meli.la, mgl.to...). Encurtador
 * generico (bit.ly) precisa ser resolvido antes por quem chama.
 */
export function forbidsThirdPartyShortener(url: string): boolean {
  const store = detectStoreHint(url);
  return store !== null && DIRECT_ONLY_STORES.includes(store);
}

/** SHEIN so converte URL canonica de produto: .../nome-p-{id}.html. */
export function isCanonicalSheinUrl(url: string): boolean {
  const parsed = parse(url);
  return Boolean(
    parsed && SHEIN_HOST.test(parsed.hostname) && SHEIN_CANONICAL_PATH.test(parsed.pathname),
  );
}

/** Magalu: atalhos (mgl.to, parceiromagalu) sempre; demais exigem pagina de produto. */
export function isMagaluProductUrl(url: string): boolean {
  const parsed = parse(url);
  if (!parsed || !MAGALU_HOST.test(parsed.hostname)) {
    return false;
  }
  const hostname = parsed.hostname.toLowerCase();
  if (
    hostname === "mgl.to" ||
    hostname.endsWith(".mgl.to") ||
    hostname.endsWith("parceiromagalu.com.br")
  ) {
    return true;
  }
  if (hostname.endsWith("magazinevoce.com.br")) {
    return MAGAZINEVOCE_PRODUCT_PATH.test(parsed.pathname);
  }
  return MAGALU_PRODUCT_PATH.test(parsed.pathname);
}

/** Lojas de rede (uma credencial cobre varias marcas, com limite de taxa). */
export function isNetworkStore(code: string): boolean {
  return code === "awin" || code === "rakuten";
}

/**
 * Regra de ToS do repo: Amazon, Mercado Livre e Magalu NUNCA passam por
 * encurtador de terceiros (link direto). Encurtar so lojas de API oficial que
 * aceitam redirect (Shopee, AliExpress, Awin, Rakuten). SHEIN segue o bot
 * (deeplink, sem encurtador).
 */
export function canShortenStore(code: string): boolean {
  return code === "shopee" || code === "aliexpress" || code === "awin" || code === "rakuten";
}

/** Parametros onde as redes guardam o destino final num link ja rastreado. */
const NETWORK_DESTINATION_PARAMS: Readonly<Record<string, string>> = {
  awin: "ued",
  rakuten: "murl",
};

/**
 * Link ja rastreado de rede (Awin cread.php?ued=..., Rakuten deeplink?murl=...)
 * de OUTRO afiliado: devolve o destino final para reconverter com a credencial
 * do tenant. null quando nao e link de rede ou nao traz destino valido.
 */
export function unwrapNetworkLink(url: string): string | null {
  const code = detectStoreCode(url);
  const param = code ? NETWORK_DESTINATION_PARAMS[code] : undefined;
  if (!param) {
    return null;
  }
  const destination = parse(url)?.searchParams.get(param) ?? null;
  return destination && parse(destination) ? destination : null;
}
